import {
  readSelectionClaims,
  recommendationFromToken,
  type LineRecommendation,
} from './recommendationProvenance';

/**
 * Recovering a basket after a reload, and nothing more.
 *
 * What is deliberately absent matters more than what is here. No prescription, no PD, no
 * measurement, no card detail, no proof image, no customer name or phone — only a customer *id*,
 * and only when one was chosen. Browser storage is readable by anything running on the origin and
 * survives the shift, the device being handed over, and the shop closing; clinical data has no
 * business in it, and a basket does not need it to be rebuilt.
 *
 * A line sold from a recommendation also keeps the recommendation's opaque signed token, whose readable payload holds ids and reason codes only, and copies of those claims for the screen. No price, score, face shape or sentence is stored.
 */
export const DRAFT_SCHEMA_VERSION = 2;
/** The layout before lines had their own ids. Still read, and upgraded in memory, so no basket saved by an earlier build is lost. */
const LEGACY_SCHEMA_VERSION = 1;
const DRAFT_TTL_MS = 12 * 60 * 60 * 1000;
/** The storage namespace, unchanged across layouts: a version 1 basket and its version 2 successor share one slot. */
const STORAGE_PREFIX = 'optiflow:basket:v1';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export interface DraftLine {
  /** The line's own identity: never its frame, never its position, because the same frame can be in a basket twice. */
  lineId: string;
  variantId: string;
  quantity: number;
  discountMinor: number;
  collectLater: boolean;
  recommendation: LineRecommendation | null;
}

export interface BasketDraft {
  schemaVersion: number;
  tenantId: string;
  branchId: string;
  userId: string;
  customerId: string | null;
  taxProfileId: string | null;
  lines: DraftLine[];
  createdAt: string;
  expiresAt: string;
}

export interface DraftScope {
  tenantId: string | null;
  branchId: string | null;
  userId: string | null;
}

/** A new basket line's id. */
export function newLineId(): string {
  return crypto.randomUUID();
}

/**
 * One slot per tenant, branch and user.
 *
 * Keyed rather than filtered so a draft cannot be surfaced to the wrong person even by accident:
 * the wrong key simply finds nothing. Two cashiers sharing a terminal each keep their own basket,
 * and a manager switching branches does not inherit the other branch's.
 */
export function draftKey(scope: DraftScope): string | null {
  if (!scope.tenantId || !scope.branchId || !scope.userId) return null;
  return `${STORAGE_PREFIX}:${scope.tenantId}:${scope.branchId}:${scope.userId}`;
}

/** Always writes the current layout, so the first save after a version 1 basket is read upgrades it. */
export function saveDraft(
  scope: DraftScope,
  input: { customerId: string | null; taxProfileId: string | null; lines: DraftLine[] },
): void {
  const key = draftKey(scope);
  if (!key) return;

  // An empty basket is not a draft worth keeping around for twelve hours.
  if (input.lines.length === 0) {
    clearDraft(scope);
    return;
  }

  const now = Date.now();
  const draft: BasketDraft = {
    schemaVersion: DRAFT_SCHEMA_VERSION,
    tenantId: scope.tenantId as string,
    branchId: scope.branchId as string,
    userId: scope.userId as string,
    customerId: input.customerId,
    taxProfileId: input.taxProfileId,
    lines: input.lines.map((line) => ({
      lineId: line.lineId,
      variantId: line.variantId,
      quantity: line.quantity,
      discountMinor: line.discountMinor,
      collectLater: line.collectLater,
      recommendation: line.recommendation
        ? {
            selectionToken: line.recommendation.selectionToken,
            customerId: line.recommendation.customerId,
            ruleVersion: line.recommendation.ruleVersion,
            faceAssessmentId: line.recommendation.faceAssessmentId,
            reasonCodes: [...line.recommendation.reasonCodes],
          }
        : null,
    })),
    createdAt: new Date(now).toISOString(),
    expiresAt: new Date(now + DRAFT_TTL_MS).toISOString(),
  };

  try {
    window.localStorage.setItem(key, JSON.stringify(draft));
  } catch {
    // A private window, or storage that is full. Losing a draft is a nuisance; failing the sale
    // over it would not be.
  }
}

/**
 * Reads the draft for exactly this context.
 *
 * Every stored field is re-checked against the caller's scope rather than trusted, because a key
 * can be edited by hand in devtools and a basket restored under the wrong tenant would be a
 * cross-tenant leak into someone's till.
 *
 * A version 1 basket is upgraded in memory — each line gets a new id and no recommendation — and left in storage exactly as it was until the counter next saves; it is never cleared for being old. The same reading applies to a basket taken back from the unresolved slot.
 */
export function loadDraft(scope: DraftScope): BasketDraft | null {
  const key = draftKey(scope);
  if (!key) return null;

  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(key);
  } catch {
    return null;
  }
  if (!raw) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    clearDraft(scope);
    return null;
  }

  const draft = readDraft(parsed);
  if (!draft) {
    clearDraft(scope);
    return null;
  }

  if (
    draft.tenantId !== scope.tenantId ||
    draft.branchId !== scope.branchId ||
    draft.userId !== scope.userId
  ) {
    clearDraft(scope);
    return null;
  }

  if (Date.parse(draft.expiresAt) <= Date.now()) {
    clearDraft(scope);
    return null;
  }

  return draft;
}

export function clearDraft(scope: DraftScope): void {
  const key = draftKey(scope);
  if (!key) return;

  try {
    window.localStorage.removeItem(key);
  } catch {
    // Nothing to be done, and nothing depends on it succeeding.
  }
}

/** Where a basket whose restore check failed waits for a later visit, beside the current slot of the same tenant, branch and user. */
export function unresolvedDraftKey(scope: DraftScope): string | null {
  const key = draftKey(scope);
  return key ? `${key}:unresolved` : null;
}

/**
 * Moves the current draft, byte for byte, into the unresolved slot, so the next basket can use the current slot without writing over it.
 *
 * Moves nothing and returns false when the unresolved slot already holds an earlier basket: one saved basket is never written over by another.
 */
export function setAsideUnresolvedDraft(scope: DraftScope): boolean {
  return moveDraft(draftKey(scope), unresolvedDraftKey(scope));
}

/** Moves an unresolved basket back into the current slot, byte for byte, when that slot is free, so it is checked the ordinary way. */
export function takeBackUnresolvedDraft(scope: DraftScope): boolean {
  return moveDraft(unresolvedDraftKey(scope), draftKey(scope));
}

/** Drops every draft on this device. For signing out. */
export function clearAllDrafts(): void {
  try {
    const keys: string[] = [];
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index);
      if (key?.startsWith(STORAGE_PREFIX)) keys.push(key);
    }
    for (const key of keys) window.localStorage.removeItem(key);
  } catch {
    // Same as above.
  }
}

function moveDraft(from: string | null, to: string | null): boolean {
  if (!from || !to) return false;

  try {
    const raw = window.localStorage.getItem(from);
    if (raw === null || window.localStorage.getItem(to) !== null) return false;
    // Written before it is removed, so an interruption between the two leaves a copy rather than nothing.
    window.localStorage.setItem(to, raw);
    window.localStorage.removeItem(from);
    return true;
  } catch {
    // Storage refused the write, so the draft is still where it was.
    return false;
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** A stored basket in either layout, as the current layout; null when it is neither. */
function readDraft(value: unknown): BasketDraft | null {
  if (!isRecord(value)) return null;
  const { schemaVersion, tenantId, branchId, userId, customerId, taxProfileId, expiresAt, lines } =
    value;

  if (
    (schemaVersion !== DRAFT_SCHEMA_VERSION && schemaVersion !== LEGACY_SCHEMA_VERSION) ||
    typeof tenantId !== 'string' ||
    typeof branchId !== 'string' ||
    typeof userId !== 'string' ||
    typeof expiresAt !== 'string' ||
    !Array.isArray(lines) ||
    !lines.every(
      (line) =>
        isRecord(line) && typeof line.variantId === 'string' && typeof line.quantity === 'number',
    )
  ) {
    return null;
  }

  const seen = new Set<string>();
  const readLines = (lines as Record<string, unknown>[]).map((line): DraftLine => {
    const variantId = line.variantId as string;
    // A version 1 line has no id yet; a version 2 line keeps its own unless it is missing, malformed or already taken, when it is given a new one rather than matched to the wrong result.
    const stored = schemaVersion === DRAFT_SCHEMA_VERSION ? line.lineId : undefined;
    const lineId =
      typeof stored === 'string' && UUID.test(stored) && !seen.has(stored) ? stored : newLineId();
    seen.add(lineId);

    return {
      lineId,
      variantId,
      quantity: line.quantity as number,
      discountMinor: typeof line.discountMinor === 'number' ? line.discountMinor : 0,
      collectLater: line.collectLater === true,
      recommendation:
        schemaVersion === DRAFT_SCHEMA_VERSION
          ? readRecommendation(line.recommendation, variantId)
          : null,
    };
  });

  return {
    schemaVersion: DRAFT_SCHEMA_VERSION,
    tenantId,
    branchId,
    userId,
    customerId: typeof customerId === 'string' ? customerId : null,
    taxProfileId: typeof taxProfileId === 'string' ? taxProfileId : null,
    lines: readLines,
    createdAt: typeof value.createdAt === 'string' ? value.createdAt : expiresAt,
    expiresAt,
  };
}

/**
 * A stored recommendation, rebuilt from its own token.
 *
 * Only the recommendation is dropped when it does not hold together — a token that cannot be read, claims for another frame, or copies that disagree with the token — never the line or the basket.
 */
function readRecommendation(value: unknown, variantId: string): LineRecommendation | null {
  if (!isRecord(value)) return null;
  const fromToken = recommendationFromToken(value.selectionToken);
  if (!fromToken) return null;

  const claimsVariant = readSelectionClaims(value.selectionToken)?.variantId;
  const copies = value.reasonCodes;
  if (
    claimsVariant !== variantId ||
    value.customerId !== fromToken.customerId ||
    value.ruleVersion !== fromToken.ruleVersion ||
    value.faceAssessmentId !== fromToken.faceAssessmentId ||
    !Array.isArray(copies) ||
    copies.length !== fromToken.reasonCodes.length ||
    !copies.every((code, index) => code === fromToken.reasonCodes[index])
  ) {
    return null;
  }
  return fromToken;
}
